import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClipboardCheck, Plus, Camera, Check, X, Calendar, Users, Filter, RefreshCw, Trash2, ChevronDown, ImageIcon, AlertCircle, Download, BarChart3, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OperationsManagement = () => {
  const { toast } = useToast();
  const { user, facility } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [view, setView] = useState('dashboard'); // dashboard | templates | reports
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const fileInputRef = useRef(null);
  const [uploadingTaskId, setUploadingTaskId] = useState(null);

  // Reports state
  const [reportPeriod, setReportPeriod] = useState('week');
  const [reportData, setReportData] = useState(null);
  const [reportStaffFilter, setReportStaffFilter] = useState('');

  // New template form
  const [newTask, setNewTask] = useState({
    name: '', description: '', facility_id: '', task_type: 'checkbox',
    frequency: 'daily', weekly_days: [], custom_dates: [], assigned_to: ''
  });

  // Load facilities for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      api.request('/facilities').then(setFacilities).catch(() => {});
    }
  }, [isSuperAdmin]);

  // Load staff for the facility
  const loadStaff = useCallback(async (facId) => {
    if (!facId) return;
    try {
      const users = await api.request(`/auth/users?facility_id=${facId}`);
      setStaffList(users || []);
    } catch {
      setStaffList([]);
    }
  }, []);

  useEffect(() => {
    const fid = isSuperAdmin ? selectedFacility : facility?.id;
    if (fid) loadStaff(fid);
  }, [isSuperAdmin, selectedFacility, facility, loadStaff]);

  // Load tasks and stats
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date: selectedDate };
      if (isSuperAdmin && selectedFacility) params.facility_id = selectedFacility;
      if (statusFilter) params.status = statusFilter;

      // Generate tasks for the date first, then fetch
      await api.generateDailyTasks(selectedDate).catch(() => {});
      const [tasksData, statsData] = await Promise.all([
        api.getTasks(params),
        api.getTaskStats({ date: selectedDate, ...(isSuperAdmin && selectedFacility ? { facility_id: selectedFacility } : {}) })
      ]);
      setTasks(tasksData || []);
      setStats(statsData);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedDate, isSuperAdmin, selectedFacility, statusFilter, toast]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const fid = isSuperAdmin && selectedFacility ? selectedFacility : undefined;
      const data = await api.getTaskTemplates(fid);
      setTemplates(data || []);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, selectedFacility, toast]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period: reportPeriod };
      if (isSuperAdmin && selectedFacility) params.facility_id = selectedFacility;
      if (reportStaffFilter) params.staff = reportStaffFilter;
      const data = await api.getOperationsReport(params);
      setReportData(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [reportPeriod, isSuperAdmin, selectedFacility, reportStaffFilter, toast]);

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (isSuperAdmin && selectedFacility) params.facility_id = selectedFacility;
      if (reportStaffFilter) params.staff = reportStaffFilter;
      if (reportPeriod === 'month') {
        const d = new Date();
        params.start_date = new Date(d - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.end_date = d.toISOString().split('T')[0];
      } else {
        const d = new Date();
        params.start_date = new Date(d - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.end_date = d.toISOString().split('T')[0];
      }
      const blob = await api.exportOperationsCSV(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `operations_report_${params.start_date}_to_${params.end_date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Report Downloaded' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  useEffect(() => {
    if (view === 'dashboard') loadDashboard();
    else if (view === 'templates') loadTemplates();
    else if (view === 'reports') loadReports();
  }, [view, loadDashboard, loadTemplates, loadReports]);

  const handleCreateTemplate = async () => {
    const facilityId = isSuperAdmin ? newTask.facility_id : facility?.id;
    if (!newTask.name || !newTask.assigned_to || !facilityId) {
      toast({ title: 'Error', description: 'Fill in all required fields', variant: 'destructive' });
      return;
    }
    try {
      await api.createTaskTemplate({ ...newTask, facility_id: facilityId });
      toast({ title: 'Task Created', description: `"${newTask.name}" added successfully` });
      setNewTask({ name: '', description: '', facility_id: '', task_type: 'checkbox', frequency: 'daily', weekly_days: [], custom_dates: [], assigned_to: '' });
      setShowCreateForm(false);
      loadTemplates();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this task template? Pending instances will also be removed.')) return;
    try {
      await api.deleteTaskTemplate(id);
      toast({ title: 'Deleted', description: 'Task template removed' });
      loadTemplates();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleCompleteTask = async (taskId, taskType) => {
    if (taskType === 'photo') {
      const task = tasks.find(t => t.id === taskId);
      if (!task?.image_url) {
        toast({ title: 'Photo Required', description: 'Upload a photo before marking complete', variant: 'destructive' });
        setUploadingTaskId(taskId);
        fileInputRef.current?.click();
        return;
      }
    }
    try {
      await api.completeTask(taskId);
      toast({ title: 'Task Completed' });
      loadDashboard();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingTaskId) return;
    try {
      await api.uploadTaskImage(uploadingTaskId, file);
      toast({ title: 'Photo Uploaded', description: 'Now mark the task as complete' });
      loadDashboard();
    } catch (err) {
      toast({ title: 'Upload Failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploadingTaskId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoAndComplete = async (taskId) => {
    setUploadingTaskId(taskId);
    fileInputRef.current?.click();
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'bg-emerald-100 text-emerald-800';
    if (status === 'missed') return 'bg-red-100 text-red-800';
    return 'bg-amber-100 text-amber-800';
  };

  const facilityId = isSuperAdmin ? selectedFacility : facility?.id;

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto" data-testid="operations-management">
      {/* Hidden file input for photo uploads */}
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImageUpload} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="operations-title">
            <ClipboardCheck className="h-6 w-6 text-blue-600" />
            Operations
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage daily venue tasks and inspections</p>
        </div>

        {/* View Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-testid="operations-tabs">
          {[{id: 'dashboard', label: "Today's Tasks"}, {id: 'templates', label: 'Task Templates'}, {id: 'reports', label: 'Reports'}].map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === v.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-${v.id}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {isSuperAdmin && (
          <select value={selectedFacility} onChange={e => setSelectedFacility(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm" data-testid="facility-filter">
            <option value="">All Venues</option>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
        {view === 'dashboard' && (
          <>
            <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
              className="w-auto text-sm" data-testid="date-filter" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm" data-testid="status-filter">
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="missed">Missed</option>
            </select>
            <Button variant="outline" size="sm" onClick={loadDashboard} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </>
        )}
        {view === 'templates' && (
          <Button onClick={() => setShowCreateForm(true)} size="sm" data-testid="create-task-btn">
            <Plus className="h-4 w-4 mr-1" /> New Task
          </Button>
        )}
      </div>

      {/* ──── DASHBOARD VIEW ──── */}
      {view === 'dashboard' && (
        <div>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="stats-cards">
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-emerald-600 uppercase tracking-wider">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-amber-600 uppercase tracking-wider">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Completion</p>
                <p className="text-2xl font-bold text-blue-600">{stats.completion_rate}%</p>
              </div>
            </div>
          )}

          {/* Task List */}
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <ClipboardCheck className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No tasks for this date</p>
              <p className="text-sm text-gray-400 mt-1">Create task templates to auto-generate daily tasks</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="task-list">
              {tasks.map(task => (
                <div key={task.id} className={`bg-white rounded-xl border p-4 flex items-center gap-4 transition-all ${task.status === 'completed' ? 'opacity-75' : ''}`} data-testid={`task-${task.id}`}>
                  {/* Status indicator */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    task.status === 'completed' ? 'bg-emerald-100' : task.status === 'missed' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    {task.status === 'completed' ? <Check className="h-5 w-5 text-emerald-600" /> :
                     task.status === 'missed' ? <X className="h-5 w-5 text-red-500" /> :
                     task.task_type === 'photo' ? <Camera className="h-5 w-5 text-gray-400" /> :
                     <ClipboardCheck className="h-5 w-5 text-gray-400" />}
                  </div>

                  {/* Task info */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-gray-900 ${task.status === 'completed' ? 'line-through' : ''}`}>{task.name}</p>
                    {task.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{task.description}</p>}
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(task.status)}`}>{task.status}</span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Users className="h-3 w-3" /> {task.assigned_to_name || task.assigned_to}
                      </span>
                      {task.task_type === 'photo' && <span className="text-xs text-blue-500 flex items-center gap-1"><Camera className="h-3 w-3" /> Photo</span>}
                    </div>
                    {task.completed_at && <p className="text-xs text-gray-400 mt-1">Done: {new Date(task.completed_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>}
                  </div>

                  {/* Photo thumbnail */}
                  {task.image_url && (
                    <a href={`${API_URL}${task.image_url}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                      <img src={`${API_URL}${task.image_url}`} alt="proof" className="w-12 h-12 rounded-lg object-cover border" />
                    </a>
                  )}

                  {/* Action buttons */}
                  {task.status === 'pending' && (
                    <div className="flex-shrink-0">
                      {task.task_type === 'photo' && !task.image_url ? (
                        <Button size="sm" variant="outline" onClick={() => handlePhotoAndComplete(task.id)} data-testid={`upload-${task.id}`}>
                          <Camera className="h-4 w-4 mr-1" /> Upload
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => handleCompleteTask(task.id, task.task_type)}
                          className="bg-emerald-600 hover:bg-emerald-700" data-testid={`complete-${task.id}`}>
                          <Check className="h-4 w-4 mr-1" /> Done
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ──── TEMPLATES VIEW ──── */}
      {view === 'templates' && (
        <div>
          {/* Create Form */}
          {showCreateForm && (
            <div className="bg-white rounded-xl border p-5 mb-6" data-testid="create-task-form">
              <h3 className="font-semibold text-gray-900 mb-4">Create Task Template</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-sm">Task Name *</Label>
                  <Input value={newTask.name} onChange={e => setNewTask(p => ({...p, name: e.target.value}))}
                    placeholder="e.g., Morning Ground Cleaning" className="mt-1" data-testid="task-name-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm">Description</Label>
                  <Input value={newTask.description} onChange={e => setNewTask(p => ({...p, description: e.target.value}))}
                    placeholder="Optional details" className="mt-1" />
                </div>
                {isSuperAdmin && (
                  <div>
                    <Label className="text-sm">Venue *</Label>
                    <select value={newTask.facility_id} onChange={e => { setNewTask(p => ({...p, facility_id: e.target.value, assigned_to: ''})); loadStaff(e.target.value); }}
                      className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="task-facility-select">
                      <option value="">Select venue</option>
                      {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <Label className="text-sm">Task Type *</Label>
                  <select value={newTask.task_type} onChange={e => setNewTask(p => ({...p, task_type: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="task-type-select">
                    <option value="checkbox">Checkbox (simple completion)</option>
                    <option value="photo">Photo (requires image upload)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm">Frequency *</Label>
                  <select value={newTask.frequency} onChange={e => setNewTask(p => ({...p, frequency: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="task-frequency-select">
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="custom">Custom Date</option>
                  </select>
                </div>
                {newTask.frequency === 'weekly' && (
                  <div className="sm:col-span-2">
                    <Label className="text-sm">Days of Week</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, i) => (
                        <button key={d} onClick={() => setNewTask(p => ({...p, weekly_days: p.weekly_days.includes(i) ? p.weekly_days.filter(x=>x!==i) : [...p.weekly_days, i]}))}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${newTask.weekly_days.includes(i) ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600'}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-sm">Assign To *</Label>
                  <select value={newTask.assigned_to} onChange={e => setNewTask(p => ({...p, assigned_to: e.target.value}))}
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm" data-testid="task-assignee-select">
                    <option value="">Select staff</option>
                    {staffList.map(s => <option key={s.email} value={s.email}>{s.name} ({s.role})</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <Button onClick={handleCreateTemplate} data-testid="save-task-btn">Create Task</Button>
                <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Templates List */}
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <ClipboardCheck className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No task templates yet</p>
              <Button onClick={() => setShowCreateForm(true)} className="mt-3" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Create First Task
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="templates-list">
              {templates.map(t => (
                <div key={t.id} className="bg-white rounded-xl border p-4 flex items-center gap-4" data-testid={`template-${t.id}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${t.task_type === 'photo' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    {t.task_type === 'photo' ? <Camera className="h-5 w-5 text-blue-600" /> : <ClipboardCheck className="h-5 w-5 text-gray-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{t.name}</p>
                    {t.description && <p className="text-xs text-gray-500 truncate">{t.description}</p>}
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{t.frequency}</span>
                      <span className="text-xs text-gray-400">{t.assigned_to_name || t.assigned_to}</span>
                      {!t.is_active && <span className="text-xs text-red-500">Inactive</span>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ──── REPORTS VIEW ──── */}
      {view === 'reports' && (
        <div data-testid="reports-view">
          {/* Report Controls */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <select value={reportPeriod} onChange={e => setReportPeriod(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm" data-testid="report-period">
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            {staffList.length > 0 && (
              <select value={reportStaffFilter} onChange={e => setReportStaffFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm" data-testid="report-staff-filter">
                <option value="">All Staff</option>
                {staffList.map(s => <option key={s.email} value={s.email}>{s.name}</option>)}
              </select>
            )}
            <Button variant="outline" size="sm" onClick={loadReports} data-testid="refresh-report-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : !reportData ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No report data available</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-summary">
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Total Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">{reportData.summary.total}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-xs text-emerald-600 uppercase tracking-wider">Completed</p>
                  <p className="text-2xl font-bold text-emerald-600">{reportData.summary.completed}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-xs text-red-600 uppercase tracking-wider">Missed</p>
                  <p className="text-2xl font-bold text-red-600">{reportData.summary.missed}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                  <p className="text-xs text-blue-600 uppercase tracking-wider">Completion Rate</p>
                  <p className="text-2xl font-bold text-blue-600">{reportData.summary.completion_rate}%</p>
                </div>
              </div>

              {/* Daily Breakdown Chart */}
              <div className="bg-white rounded-xl border p-5" data-testid="daily-breakdown">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                  Daily Completion Trend
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {reportData.daily_breakdown.map(day => (
                    <div key={day.date} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-20 flex-shrink-0">
                        {new Date(day.date + 'T00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden flex">
                        <div className="bg-emerald-500 h-full transition-all" style={{ width: `${day.total > 0 ? (day.completed / day.total) * 100 : 0}%` }} />
                        <div className="bg-red-400 h-full transition-all" style={{ width: `${day.total > 0 ? (day.missed / day.total) * 100 : 0}%` }} />
                      </div>
                      <span className="text-xs font-medium text-gray-600 w-12 text-right">{day.completion_rate}%</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded-sm inline-block" /> Completed</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded-sm inline-block" /> Missed</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-100 rounded-sm inline-block" /> Pending</span>
                </div>
              </div>

              {/* Staff Performance */}
              {reportData.staff_performance.length > 0 && (
                <div className="bg-white rounded-xl border p-5" data-testid="staff-performance">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    Staff Performance
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 font-medium text-gray-500">Staff</th>
                          <th className="pb-2 font-medium text-gray-500 text-center">Total</th>
                          <th className="pb-2 font-medium text-gray-500 text-center">Done</th>
                          <th className="pb-2 font-medium text-gray-500 text-center">Missed</th>
                          <th className="pb-2 font-medium text-gray-500 text-center">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.staff_performance.map(s => (
                          <tr key={s.email} className="border-b last:border-0">
                            <td className="py-2.5 font-medium text-gray-900">{s.name}</td>
                            <td className="py-2.5 text-center text-gray-600">{s.total}</td>
                            <td className="py-2.5 text-center text-emerald-600 font-medium">{s.completed}</td>
                            <td className="py-2.5 text-center text-red-500">{s.missed}</td>
                            <td className="py-2.5 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                s.completion_rate >= 90 ? 'bg-emerald-100 text-emerald-700' :
                                s.completion_rate >= 70 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {s.completion_rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Frequently Missed Tasks */}
              {reportData.frequently_missed.length > 0 && (
                <div className="bg-white rounded-xl border p-5" data-testid="frequently-missed">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    Frequently Missed Tasks
                  </h3>
                  <div className="space-y-2">
                    {reportData.frequently_missed.map((t, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                        <span className="text-sm text-gray-700">{t.name}</span>
                        <span className="text-sm font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                          {t.count}x missed
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OperationsManagement;
